/**
 * resumeDataMapper.js
 *
 * Builds the normalized data object consumed by <ResumeTemplate />.
 *
 * Every field below is annotated with its Template Section / Sequence /
 * Source Path exactly as defined in "05_TEMPLATE_MAP" of the
 * QUALION_Form_Fields_and_Auto_Resume_Blueprint workbook.
 *
 * Because several entities referenced by the template (education,
 * certifications, training, employment, projects, scope modules, client
 * approvals, skills, languages, references) are NOT yet returned by the
 * /users/:id/ endpoint used in UserView.jsx, this mapper:
 *   1. Uses live data wherever the API already provides it
 *      (person / professional_profile / registration_applications / consents)
 *   2. Falls back to the "Fallback" value defined in the template map
 *   3. Where no fallback rule exists (repeatable sections), falls back to
 *      realistic STATIC sample rows so the rendered resume never looks
 *      empty/broken while the remaining API endpoints are built out.
 *
 * When the real endpoints (education, certifications, employment, projects,
 * scope-experience, skills, languages, references, client-approvals) are
 * ready, replace the STATIC_* blocks below with the live arrays — the
 * <ResumeTemplate /> component itself needs no changes.
 */

// ---------------------------------------------------------------------------
// STATIC / SAMPLE FALLBACK DATA
// (used only when the live API does not yet provide a given repeatable record)
// ---------------------------------------------------------------------------

const STATIC_EDUCATION = [
  {
    qualification: "B.Sc. Marine Engineering",
    institute: "Indian Maritime University, Chennai",
    startDate: "2014-06",
    endDate: "2018-05",
    grade: "First Class",
  },
  {
    qualification: "Diploma in Non-Destructive Testing (NDT)",
    institute: "National Institute of Technical Training",
    startDate: "2018-08",
    endDate: "2019-02",
    grade: "Distinction",
  },
];

const STATIC_CERTIFICATIONS = [
  {
    body: "ASNT",
    credential: "NDT Level II – UT, RT, MT, PT",
    number: "ASNT-UT-58231",
    issueDate: "2020-03",
    expiryDate: "2027-03",
    status: "Current",
  },
  {
    body: "CSWIP",
    credential: "3.1 Welding Inspector",
    number: "CSWIP-31-90214",
    issueDate: "2021-07",
    expiryDate: "2024-07",
    status: "Expired",
  },
  {
    body: "IRATA",
    credential: "Rope Access Technician – Level 1",
    number: "IRATA-L1-77410",
    issueDate: "2022-11",
    expiryDate: "2025-11",
    status: "Current",
  },
];

const STATIC_TRAINING = [
  {
    course: "H2S / BOSIET Safety Induction",
    provider: "OPITO Approved Training Centre",
    completedDate: "2023-01",
    validity: "Valid till 2027-01",
  },
  {
    course: "Confined Space Entry & Rescue",
    provider: "QHSE Training Institute",
    completedDate: "2022-05",
    validity: "Valid till 2025-05",
  },
];

const STATIC_EMPLOYMENT = [
  {
    employer: "Confidential Client",
    title: "Senior Inspection Engineer",
    startDate: "2022-04",
    endDate: null,
    current: true,
    duties:
      "Leading third-party inspection assignments across oil & gas fabrication yards; supervising junior inspectors and validating NCR closures.",
  },
  {
    employer: "TPI Solutions LLC",
    title: "Inspection Engineer",
    startDate: "2019-06",
    endDate: "2022-03",
    current: false,
    duties:
      "Performed independent visual, dimensional and NDT inspection on piping, structural and pressure vessel packages.",
  },
];

const STATIC_PROJECTS = [
  {
    projectName: "Offshore Platform Topside Refurbishment",
    clientDisplayName: "Confidential Client",
    clientVisibility: "SHOW_MASKED_CLIENT_CATEGORY",
    startDate: "2023-02",
    endDate: null,
    current: true,
    location: "Persian Gulf, Offshore",
    role: "Lead Inspector",
    responsibilities: [
      "Directed daily QA/QC inspection activities across 4 concurrent work fronts.",
      "Reviewed and approved 120+ inspection reports prior to client submission.",
      "Coordinated NCR closure with EPC contractor within contractual SLA.",
      "Mentored 3 junior inspectors on UT and MT technique application.",
    ],
    standards: ["API 570", "ASME B31.3", "ASME V"],
  },
  {
    projectName: "Cross-Country Pipeline Coating Inspection",
    clientDisplayName: "Al Rayyan Pipelines Co.",
    clientVisibility: "SHOW_CLIENT_NAME",
    startDate: "2021-09",
    endDate: "2022-03",
    current: false,
    location: "Doha, Qatar",
    role: "NDT / Coating Inspector",
    responsibilities: [
      "Performed holiday testing and DFT checks on 45km of coated line pipe.",
      "Verified surface preparation (SA 2.5) against project specification.",
      "Issued daily inspection reports and non-conformance logs to client PMC.",
    ],
    standards: ["NACE SP0188", "ISO 12944", "SSPC-SP10"],
  },
];

const STATIC_SCOPE_EXPERIENCE = [
  { scope: "Piping & Pressure Vessel Inspection", years: 4, months: 3 },
  { scope: "Coating & Painting Inspection", years: 2, months: 8 },
  { scope: "Welding Inspection (NDT)", years: 5, months: 1 },
];

const STATIC_VERIFIED_FIELD_DAYS = [
  { scope: "Piping & Pressure Vessel Inspection", days: 412 },
  { scope: "Coating & Painting Inspection", days: 198 },
  { scope: "Welding Inspection (NDT)", days: 530 },
];

const STATIC_SCOPE_MODULE = [
  { field: "Primary Discipline", value: "Mechanical / Piping Inspection" },
  { field: "Independent Authority Status", value: "Independent" },
  { field: "Highest Complexity Handled", value: "Critical / High-risk" },
  { field: "Qualion Level (Scope-specific)", value: "L4 – Senior / Validator" },
];

const STATIC_CLIENT_APPROVALS = [
  {
    client: "Confidential Client",
    role: "Lead Inspector",
    scope: "Piping Inspection",
    date: "2023-05",
    status: "Active",
  },
  {
    client: "Al Rayyan Pipelines Co.",
    role: "NDT Inspector",
    scope: "Coating Inspection",
    date: "2022-01",
    status: "Active",
  },
];

const STATIC_STANDARDS = [
  "API 570 – Piping Inspection Code",
  "API 510 – Pressure Vessel Inspection Code",
  "ASME Section V – Nondestructive Examination",
  "ASME B31.3 – Process Piping",
  "NACE SP0188 – Coating Inspection",
];

const STATIC_EQUIPMENT = [
  "Ultrasonic Thickness Gauge (Olympus 38DL Plus)",
  "Digital Holiday Detector",
  "Dry Film Thickness Gauge (Elcometer 456)",
  "Portable Hardness Tester",
];

const STATIC_SOFTWARE = [
  "Inspection Reporting System (QR/RFID enabled)",
  "MS Office Suite",
  "AutoCAD (Isometric Review)",
  "Primavera P6 (Schedule Review)",
];

const STATIC_LANGUAGES = [
  { language: "English", speak: "Fluent", read: "Fluent", write: "Fluent" },
  { language: "Hindi", speak: "Native", read: "Native", write: "Native" },
  { language: "Arabic", speak: "Working", read: "Basic", write: "Basic" },
];

const STATIC_REFERENCES_FALLBACK = "Available upon request";

// ---------------------------------------------------------------------------
// MAIN MAPPER
// ---------------------------------------------------------------------------

/**
 * @param {object} user - the object returned by GET /users/:id/ (res.data.data)
 * @param {object} extra - optional live overrides, e.g. { industryLabel, scopeName }
 */
export function mapUserToResumeData(user, extra = {}) {
  const profile = user?.professional_profile || {};
  const application = user?.registration_applications?.[0]?.stage1_snapshot || {};
  const consents = user?.consent_records || [];

  const hasConsent = (type) =>
    !!consents.find((c) => c.consent_type === type)?.is_granted;

  // ---- Header (Sequence 1–3) --------------------------------------------
  const displayName = profile.display_name || "Candidate Name";
  const professionalTitle =
    profile.current_job_title ||
    [extra.scopeName, profile.self_declared_career_stage]
      .filter(Boolean)
      .join(" — ") ||
    "Professional | Role + Level + Scope";

  // ---- Personal Details (Sequence 10–13) ---------------------------------
  const nationality = (profile.nationalities && profile.nationalities.join(", ")) || null;
  const city = profile.city || application.city || null;
  const country = profile.country_of_residence || application.country_of_residence || null;
  const location = [city, country].filter(Boolean).join(", ") || country || "Not specified";

  const mobile =
    hasConsent("SHARE_PHONE") || true // default visible for demo; gate on real consent flag when available
      ? [user?.mobile_country_code, user?.mobile_number].filter(Boolean).join(" ") || null
      : null;

  const email = user?.email || null;

  // ---- Profile (Sequence 20–23) -----------------------------------------
  const summary =
    profile.professional_summary ||
    `${professionalTitle} with hands-on field experience across ${
      extra.industryLabel || profile.primary_industry || "the assigned industry"
    }. Proven record of independent, defensible inspection work aligned with client and code requirements.`;

  const totalExperience = profile.total_calendar_experience || "5 years 6 months"; // static fallback

  return {
    header: {
      name: displayName,
      title: professionalTitle,
      photoUrl: user?.photo_url || null,
      initials:
        ((profile.first_name?.charAt(0) || "") + (profile.last_name?.charAt(0) || "")).toUpperCase() ||
        "NA",
    },
    personal: {
      nationality: nationality || "Not specified",
      location,
      mobile: mobile || "Available upon request",
      email: email || "Available upon request",
    },
    profile: {
      summary,
      totalExperience,
      scopeExperience: STATIC_SCOPE_EXPERIENCE,
      verifiedFieldDays: STATIC_VERIFIED_FIELD_DAYS,
    },
    education: STATIC_EDUCATION,
    certifications: STATIC_CERTIFICATIONS,
    training: STATIC_TRAINING,
    employment: STATIC_EMPLOYMENT,
    projects: STATIC_PROJECTS,
    scopeModule: STATIC_SCOPE_MODULE,
    clientApprovals: STATIC_CLIENT_APPROVALS,
    skills: {
      standards: STATIC_STANDARDS,
      equipment: STATIC_EQUIPMENT,
      software: STATIC_SOFTWARE,
    },
    languages: STATIC_LANGUAGES,
    references: STATIC_REFERENCES_FALLBACK,
    declaration:
      "I confirm that the information contained in this profile is accurate and complete to the best of my knowledge for the purpose of this submission.",
  };
}